import type { AxiosResponse } from "axios";

import { deleteData, getData, postData } from "@/apis/apiInstance";

import type {
  AddApprovalPayloadType,
  AddOpenPositionPayloadType,
  AddRecruitmentTrackerPayloadType,
  ApprovalListPayloadType,
  DeleteOpenPositionPayloadType,
  DownloadRecruitmentTrackerPayloadType,
  GetHrmsBulkUploadHistoryPayloadType,
  GetOpenPositionPayloadType,
  GetRecruitmentTrackerPayloadType,
  GetResignationUpdatesListPayloadType,
  GetSingleRecruitmentTrackerPayloadType,
  RecruitmentTrackerBulkUploadType,
  SubmitHikeReasonPayload,
  SendFeedbackApprovalPayloadType,
  UpdateHiringCheckListPayloadType,
  UpdateOpenPositionPayloadType,
  GetWeightedAverageReportPayloadType,
} from "@/commonTypes/hrmsTypes";

import { getCSRFToken, reverseDate } from "@/utils/commonFunctions";

export async function createRecruitmentModuleOpenPositionApi(
  data: AddOpenPositionPayloadType
): Promise<AxiosResponse> {
  const { token, position_name, department_id, status } = data;

  return await postData(
    `/finac-crm/hrms/recruitment-application/open-job-position/add`,
    {
      position_name,
      department_id,
      status,
    },
    {
      headers: {
        "Content-Type": "application/json",
        "user-token": token,
        "x-csrf-token": getCSRFToken(),
      },
    }
  );
}

export async function getRecruitmentModuleOpenPositionApi(
  data: GetOpenPositionPayloadType
): Promise<AxiosResponse> {
  const { token, limit, offset, search_term, status } = data;
  const finalParams = {
    limit,
    offset,
    status,
    ...(search_term && search_term.length > 0 && { search_term: search_term }),
  };
  return await getData(
    `/finac-crm/hrms/recruitment-application/open-job-position/list`,
    {
      params: {
        ...finalParams,
      },
      headers: {
        "Content-Type": "application/json",
        "user-token": token,
      },
    }
  );
}
export async function updateRecruitmentModuleOpenPositionApi(
  data: UpdateOpenPositionPayloadType
): Promise<AxiosResponse> {
  const { token, position_id, position_name, department_id, status } = data;
  return await postData(
    `/finac-crm/hrms/recruitment-application/open-job-position/update`,
    { position_id, department_id, position_name, status },
    {
      headers: {
        "Content-Type": "application/json",
        "user-token": token,
        "x-csrf-token": getCSRFToken(),
      },
    }
  );
}

export async function deleteRecruitmentModuleOpenPositionApi(
  data: DeleteOpenPositionPayloadType
): Promise<AxiosResponse> {
  const { token, id } = data;

  const finalData = { id: id };

  return deleteData(
    `finac-crm/hrms/recruitment-application/open-job-position/delete`,
    {
      params: {
        ...finalData,
      },
      headers: {
        "Content-Type": "application/json",
        "user-token": token,
        "x-csrf-token": getCSRFToken(),
      },
    }
  );
}

export async function getRecruitmentTrackerListApi(
  data: GetRecruitmentTrackerPayloadType
): Promise<AxiosResponse> {
  const {
    token,
    limit,
    offset,
    search_term,
    progress,
    sortBy,
    sortingOrder,
    recruiter,
    jobLocation,
    startDate,
    endDate,
    experience,
    gender,
    openPosition,
  } = data;

  const finalParams = {
    limit,
    offset,
    progress,
    recruiter,
    gender,
    job_location: jobLocation,
    start_date: startDate,
    end_date: endDate,
    job_position: openPosition,
    experience,
    sort_by: sortBy,
    sorting_order: sortingOrder,
    ...(search_term && search_term.length > 0 && { search_term: search_term }),
  };
  return await getData(`v2/finac-crm/hrms/recruitment-application/list`, {
    params: {
      ...finalParams,
    },
    headers: {
      "Content-Type": "application/json",
      "user-token": token,
    },
  });
}

export async function getDeprecatedRecruitmentTrackerListApi(
  data: GetRecruitmentTrackerPayloadType
): Promise<AxiosResponse> {
  const {
    token,
    limit,
    offset,
    search_term,
    progress,
    sortBy,
    sortingOrder,
    recruiter,
    jobLocation,
    startDate,
    endDate,
    experience,
    gender,
    openPosition,
  } = data;

  const finalParams = {
    limit,
    offset,
    progress,
    recruiter,
    gender,
    job_location: jobLocation,
    start_date: startDate,
    end_date: endDate,
    job_position: openPosition,
    experience,
    sort_by: sortBy,
    sorting_order: sortingOrder,
    ...(search_term && search_term.length > 0 && { search_term: search_term }),
  };
  return await getData(`/finac-crm/hrms/recruitment-application/list`, {
    params: {
      ...finalParams,
    },
    headers: {
      "Content-Type": "application/json",
      "user-token": token,
    },
  });
}

export async function getRecruitmentTrackerStageCountApi(
  data: GetRecruitmentTrackerPayloadType
): Promise<AxiosResponse> {
  const {
    token,
    limit,
    offset,
    search_term,
    progress,
    sortBy,
    sortingOrder,
    recruiter,
    jobLocation,
    startDate,
    endDate,
    experience,
    gender,
    openPosition,
  } = data;
  const finalParams = {
    limit,
    offset,
    progress,
    recruiter,
    gender,
    job_location: jobLocation,
    start_date: startDate,
    end_date: endDate,
    job_position: openPosition,
    experience,
    sort_by: sortBy,
    sorting_order: sortingOrder,
    ...(search_term && search_term.length > 0 && { search_term: search_term }),
  };
  return await getData(
    `v2/finac-crm/hrms/recruitment-application/list/stage-count`,
    {
      params: {
        ...finalParams,
      },
      headers: {
        "Content-Type": "application/json",
        "user-token": token,
      },
    }
  );
}

export async function addRecruitmentTrackerApi(
  data: AddRecruitmentTrackerPayloadType
): Promise<AxiosResponse> {
  const { token, candidateDetails } = data;
  const finalData = {
    is_user_created: candidateDetails?.is_user_created,
    applicant_details: {
      profile_pic: candidateDetails?.applicantDetails?.profile_pic,
      name: candidateDetails?.applicantDetails?.name?.trim(),
      email: candidateDetails?.applicantDetails?.email?.trim(),
      gender: candidateDetails?.applicantDetails?.gender?.value,
      skill: candidateDetails?.applicantDetails?.skill?.map(
        (item: any) => item.value
      ),
      qualification: candidateDetails?.applicantDetails?.qualification?.map(
        (item: any) => item.value
      ),
      phone_number: candidateDetails?.applicantDetails?.phoneNumber?.trim(),
      years_of_experience:
        candidateDetails?.applicantDetails?.yearsOfExperience,
      linkedin_profile:
        candidateDetails?.applicantDetails?.linkedinProfile?.trim(),
      notice_period: candidateDetails?.applicantDetails?.noticePeriod
        ? candidateDetails?.applicantDetails?.noticePeriod?.toString()?.trim()
        : "",
      resume_upload: {
        file_type: candidateDetails?.applicantDetails?.resume?.value?.file_type,
        file_name: candidateDetails?.applicantDetails?.resume?.value?.file_name,
        file_url: candidateDetails?.applicantDetails?.resume?.value?.file_url,
      },
    },
    position_details: {
      department: candidateDetails?.positionDetails?.department?.value,
      job_position: candidateDetails?.positionDetails?.jobPosition?.value,
      job_location: candidateDetails?.positionDetails?.jobLocation?.value,
      progress: candidateDetails?.positionDetails?.progress?.label,
      recruiter: candidateDetails?.positionDetails?.recruiter?.value,
      offered_job_position:
        candidateDetails?.positionDetails?.offeredJobPosition?.value,
      proposed_joining_date:
        candidateDetails?.positionDetails?.proposedJoiningDate,
      job_position_requested_by:
        candidateDetails?.positionDetails?.jobPositionRequestedBy?.value,
    },
    interview_details: {
      application_date: candidateDetails?.interviewDetails?.applicationDate
        ? reverseDate(
            candidateDetails?.interviewDetails?.applicationDate?.split("T")[0]
          )
        : null,
      screening_details: {
        phone_screening_date_and_time: candidateDetails?.interviewDetails
          ?.phoneScreeningDateAndTime
          ? candidateDetails?.interviewDetails?.phoneScreeningDateAndTime
          : null,
        phone_screener:
          candidateDetails?.interviewDetails?.phoneScreener?.value,
        screening_feedback:
          candidateDetails?.interviewDetails?.interviewFeedback,
      },
      interview_round_details:
        candidateDetails?.interviewDetails?.interviewInputDetails?.map(
          (round: any) => ({
            interview_date_and_time: round?.interviewDateAndTime
              ? round?.interviewDateAndTime
              : null,
            interviewer: round?.interviewers?.value,
            interview_feedback: round?.interviewFeedback,
          })
        ),
    },

    salary_details: {
      current_total_ctc: candidateDetails?.salary_details?.currentTotalCtc,
      current_fixed_ctc: candidateDetails?.salary_details?.current_fixed_ctc,
      min_expected_total_ctc:
        candidateDetails?.salary_details?.min_expected_total_ctc,
      min_expected_fixed_ctc:
        candidateDetails?.salary_details?.min_expected_fixed_ctc,
      offered_ctc: candidateDetails?.salary_details?.offered_annual_ctc,
      offered_fixed_ctc:
        candidateDetails?.salary_details?.offered_fixed_annual_ctc,
    },
    other_details: {
      referred_by: candidateDetails?.otherDetails?.referredBy?.value,
      sources: candidateDetails?.otherDetails?.sources?.value,
      meeting_link: candidateDetails?.otherDetails?.meetingLink?.trim(),
    },
  };
  return await postData(
    `finac-crm/hrms/recruitment-application/add`,
    finalData,
    {
      headers: {
        "Content-Type": "application/json",
        "user-token": token,
        "x-csrf-token": getCSRFToken(),
      },
    }
  );
}

export async function updateRecruitmentTrackerListApi(
  data: AddRecruitmentTrackerPayloadType
): Promise<AxiosResponse> {
  const { token, candidateDetails, id } = data;

  const finalData = {
    id: id,
    is_user_created: candidateDetails?.is_user_created,
    applicant_details: {
      profile_pic: candidateDetails?.applicantDetails?.profile_pic,
      name: candidateDetails?.applicantDetails?.name?.trim(),
      email: candidateDetails?.applicantDetails?.email?.trim(),
      phone_number: candidateDetails?.applicantDetails?.phoneNumber?.trim(),
      gender: candidateDetails?.applicantDetails?.gender?.value,
      skill: candidateDetails?.applicantDetails?.skill?.map(
        (item: any) => item.value
      ),
      qualification: candidateDetails?.applicantDetails?.qualification?.map(
        (item: any) => item.value
      ),
      years_of_experience:
        candidateDetails?.applicantDetails?.yearsOfExperience,
      linkedin_profile:
        candidateDetails?.applicantDetails?.linkedinProfile?.trim(),
      notice_period: candidateDetails?.applicantDetails?.noticePeriod
        ? candidateDetails?.applicantDetails?.noticePeriod?.toString()?.trim()
        : "",
      resume_upload: {
        file_type: candidateDetails?.applicantDetails?.resume?.value?.file_type,
        file_name: candidateDetails?.applicantDetails?.resume?.value.file_name,
        file_url: candidateDetails?.applicantDetails?.resume?.value?.file_url,
      },
    },
    position_details: {
      department: candidateDetails?.positionDetails?.department?.value,
      job_position: candidateDetails?.positionDetails?.jobPosition?.value,
      job_location: candidateDetails?.positionDetails?.jobLocation?.value,
      progress: candidateDetails?.positionDetails?.progress?.label,
      recruiter: candidateDetails?.positionDetails?.recruiter?.value,
      offered_job_position:
        candidateDetails?.positionDetails?.offeredJobPosition?.value,
      proposed_joining_date: candidateDetails?.positionDetails
        ?.proposedJoiningDate
        ? candidateDetails?.positionDetails?.proposedJoiningDate
        : null,
      job_position_requested_by:
        candidateDetails?.positionDetails?.jobPositionRequestedBy?.value,
    },
    interview_details: {
      application_date: candidateDetails?.interviewDetails?.applicationDate
        ? reverseDate(
            candidateDetails?.interviewDetails?.applicationDate?.split("T")[0]
          )
        : null,
      screening_details: {
        phone_screening_date_and_time: candidateDetails?.interviewDetails
          ?.phoneScreeningDateAndTime
          ? candidateDetails?.interviewDetails?.phoneScreeningDateAndTime
          : null,
        phone_screener:
          candidateDetails?.interviewDetails?.phoneScreener?.value,
        screening_feedback:
          candidateDetails?.interviewDetails?.interviewFeedback,
      },
      interview_round_details:
        candidateDetails?.interviewDetails?.interviewInputDetails?.map(
          (round: any) => ({
            interview_date_and_time: round?.interviewDateAndTime
              ? round?.interviewDateAndTime
              : null,
            interviewer: round?.interviewers?.value,
            interview_feedback: round?.interviewFeedback,
            is_feedback_submited: round?.is_feedback_submited,
            level: round?.level,
          })
        ),
    },

    salary_details: {
      current_total_ctc: candidateDetails?.salary_details?.currentTotalCtc,
      current_fixed_ctc: candidateDetails?.salary_details?.current_fixed_ctc,
      min_expected_total_ctc:
        candidateDetails?.salary_details?.min_expected_total_ctc,
      min_expected_fixed_ctc:
        candidateDetails?.salary_details?.min_expected_fixed_ctc,
      offered_ctc: candidateDetails?.salary_details?.offered_annual_ctc,
      offered_fixed_ctc:
        candidateDetails?.salary_details?.offered_fixed_annual_ctc,
      offered_ctc_percentage:
        candidateDetails?.salary_details?.offeredCtcPercentage ?? 0,
      offered_fixed_ctc_percentage:
        candidateDetails?.salary_details?.offeredFixedCtcPercentage ?? 0,
    },
    other_details: {
      referred_by: candidateDetails?.otherDetails?.referredBy?.value,
      sources: candidateDetails?.otherDetails?.sources?.value,
      meeting_link: candidateDetails?.otherDetails?.meetingLink?.trim(),
    },
  };
  return await postData(
    `finac-crm/hrms/recruitment-application/update`,
    finalData,
    {
      headers: {
        "Content-Type": "application/json",
        "user-token": token,
        "x-csrf-token": getCSRFToken(),
      },
    }
  );
}

export async function getSingleRecruitmentTrackerListApi(
  data: GetSingleRecruitmentTrackerPayloadType
): Promise<AxiosResponse> {
  const { token, id, type } = data;
  return await getData(`finac-crm/hrms/recruitment-application/list/id`, {
    params: {
      id,
      type,
    },
    headers: {
      "Content-Type": "application/json",
      "user-token": token,
    },
  });
}

export async function deleteRecruitmentTrackerApi(
  data: DeleteOpenPositionPayloadType
): Promise<AxiosResponse> {
  const { token, id } = data;

  const finalData = { id: id };

  return deleteData(`finac-crm/hrms/recruitment-application/delete`, {
    params: {
      ...finalData,
    },
    headers: {
      "Content-Type": "application/json",
      "user-token": token,
      "x-csrf-token": getCSRFToken(),
    },
  });
}

export async function updateHiringCheckListDataApi(
  data: UpdateHiringCheckListPayloadType
): Promise<AxiosResponse> {
  const { token, hiringCheckListData, id } = data;

  const finalData = {
    id: id,
    comp_discussed_accepted_email:
      hiringCheckListData?.compDiscussedAndAccepted,
    reference_call_feedback_received:
      hiringCheckListData?.referenceCallFeedbackReceived,
    background_check_complete_within_30_days:
      hiringCheckListData?.within30DaysCompletedBackgroundCheck,
    documentation: hiringCheckListData?.documentation,
    hris_form_filled: hiringCheckListData?.filingUpHRISFormAndLaptopForm,
    laptop_form_filled: hiringCheckListData?.filingUpHRISFormAndLaptopForm,
    offer_letter_signed: hiringCheckListData?.offerLetterSignature,
    finac_knowledge: hiringCheckListData?.finacKnowledge,
    holiday_list: hiringCheckListData?.holidayList,
    email_id_creation: hiringCheckListData?.emailIDCreation,
    hr_policies: hiringCheckListData?.hRPolicies,
    pto_tool: hiringCheckListData?.ptoTool,
    stationary_items: hiringCheckListData?.stationaryItems,
    passbook_bank_statement_xerox: hiringCheckListData?.passBookBankXerox,
    payslip_last_two_months: hiringCheckListData?.payslip,
    appointment_letter_previous_org: hiringCheckListData?.appointmentLetter,
    offer_letter_previous_org: hiringCheckListData?.offerLetter,
    relieving_letter_previous_org: hiringCheckListData?.relievingLetter,
    experience_letter_previous_org: hiringCheckListData?.experienceLetter,
    icai_intimation: hiringCheckListData?.iCAIIntimation?.trim(),
    employee_code: hiringCheckListData?.employeeCode,
    income_tax_return: hiringCheckListData?.incomeTaxReturn,
    file_attachment: hiringCheckListData?.fileAttachements,
  };
  return await postData(`finac-crm/check-list/update`, finalData, {
    headers: {
      "Content-Type": "application/json",
      "user-token": token,
      "x-csrf-token": getCSRFToken(),
    },
  });
}

export async function getApprovalListActionApi(
  data: ApprovalListPayloadType
): Promise<AxiosResponse> {
  const {
    token,
    limit,
    offset,
    search_term,
    hrms_id,
    approver_id,
    today,
    type,
    sort_by,
    sorting_order,
    status,
    location,
    position,
    recruiter,
    start_date,
    end_date,
    progress,
  } = data;
  const finalParams = {
    limit,
    offset,
    approver_id,
    hrms_id,
    today,
    type,
    sort_by,
    sorting_order,
    status,
    location,
    position,
    recruiter,
    start_date,
    end_date,
    progress,
    ...(search_term && search_term.length > 0 && { search_term: search_term }),
  };
  return await getData(`/finac-crm/things-to-do/approval/list`, {
    params: {
      ...finalParams,
    },
    headers: {
      "Content-Type": "application/json",
      "user-token": token,
    },
  });
}

export async function addApprovalActionApi(
  data: AddApprovalPayloadType
): Promise<AxiosResponse> {
  const {
    token,
    hrms_id,
    approved_by,
    reporting_manager,
    assigned_coach,
    assigned_clients,
  } = data;

  const finalData = {
    hrms_id,
    approved_by,
    reporting_manager,
    assigned_coach,
    assigned_clients,
  };
  return await postData(`finac-crm/things-to-do/approval/add`, finalData, {
    headers: {
      "Content-Type": "application/json",
      "user-token": token,
      "x-csrf-token": getCSRFToken(),
    },
  });
}

// download recruitment tracker
export async function downloadApplicationInProgressExcelApi(
  data: DownloadRecruitmentTrackerPayloadType
): Promise<AxiosResponse> {
  const {
    token,
    progress,
    sortBy,
    sortingOrder,
    recruiter,
    jobLocation,
    startDate,
    endDate,
    experience,
    gender,
    openPosition,
    searchTerm,
  } = data;

  return await getData(
    `finac-crm/hrms/recruitment-application/excel-download`,
    {
      params: {
        progress,
        recruiter,
        gender,
        job_location: jobLocation,
        start_date: startDate,
        end_date: endDate,
        job_position: openPosition,
        experience,
        sort_by: sortBy,
        sorting_order: sortingOrder,
        search_term: searchTerm,
      },
      responseType: "blob",
      headers: {
        "Content-Type": "application/json",
        "user-token": token,
      },
    }
  );
}
// BULK UPLOAD
export async function recruitmentTrackerBulkUploadApi(
  data: RecruitmentTrackerBulkUploadType
): Promise<AxiosResponse> {
  const { token, fileData } = data;

  const formData = new FormData();
  formData.append("file", fileData);

  return postData(`finac-crm/hrms/recruitment-application/upload`, formData, {
    headers: {
      "user-token": token,
      "x-csrf-token": getCSRFToken(),
    },
  });
}
// BULK UPLOAD HISTORY

export async function getRecruitmentTrackerBulkUploadHistoryData(
  data: GetHrmsBulkUploadHistoryPayloadType
): Promise<AxiosResponse> {
  const { token, limit, offset } = data;

  return await getData(
    `/finac-crm/hrms/recruitment-application/bulk-upload/history`,
    {
      params: {
        limit,
        offset,
      },
      headers: {
        "Content-Type": "application/json",
        "user-token": token,
      },
    }
  );
}

export const submitHikeReasonApi = async (
  data: SubmitHikeReasonPayload
): Promise<AxiosResponse> => {
  const { token, hrms_id, hikeReason } = data;

  return await postData(
    `/finac-crm/hrms/recruitment-application/add/hike-reason`,
    {
      hrms_id,
      hike_reason: hikeReason,
    },
    {
      headers: {
        "Content-Type": "application/json",
        "user-token": token,
        "x-csrf-token": getCSRFToken(),
      },
    }
  );
};
// Resignation Updates List
export async function getResignationUpdatesListApi(
  data: GetResignationUpdatesListPayloadType
): Promise<AxiosResponse> {
  const {
    token,
    limit,
    offset,
    approverId,
    search_term,
    department,
    requestStatus,
    employmentStatus,
    noticePeriod,
    sortBy,
  } = data;

  return await getData(`finac-crm/hrms/resignation/list`, {
    params: {
      limit,
      offset,
      approver_id: approverId,
      ...(search_term && search_term?.length > 0 && { search_term }),
      department_filter: department,
      request_status: requestStatus,
      employment_status: employmentStatus,
      notice_period: noticePeriod,
      sort_by: sortBy,
    },
    headers: {
      "Content-Type": "application/json",
      "user-token": token,
    },
  });
}

// send feedback approval
export async function sendFeedbackApprovalApi(
  data: SendFeedbackApprovalPayloadType
): Promise<AxiosResponse> {
  const { token, approverId, resignationId, isNewApprover } = data;

  const finalData = {
    approver_id: approverId,
    resignation_id: resignationId,
    is_new_approver: isNewApprover,
  };
  return await postData(
    `finac-crm/hrms/resignation/send/feedback/approval`,
    finalData,
    {
      headers: {
        "Content-Type": "application/json",
        "user-token": token,
        "x-csrf-token": getCSRFToken(),
      },
    }
  );
}

export async function getWeightedAverageReportApi(
  data: GetWeightedAverageReportPayloadType
): Promise<AxiosResponse> {
  const { token, start_date, end_date, limit, offset, user_id, department } =
    data;

  const params = {
    limit,
    offset,
    start_date,
    end_date,
    user_id,
    department,
  };

  return await getData(`/finac-crm/hrms/weighted-avg/report`, {
    params,
    headers: {
      "Content-Type": "application/json",
      "user-token": token,
    },
  });
}
